import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { MainLayout } from 'views/layouts'
import {
  Container,
  MyProcessContainer,
  BigContainer,
  TopContainer,
  MetricsGrid,
  MetricCard,
  MetricLabel,
  MetricValue,
  MetricIcon,
  AnalyticsCard,
  AnalyticsCardTitle,
  AnalyticsCardSubtitle,
  BarsList,
  BarRow,
  BarLabel,
  BarTrack,
  BarFill,
  BarValue,
  ProgressValue,
  ProgressCaption,
  ProgressTrack,
  ProgressFill,
  ProgressLegend,
  LegendItem,
  LegendDot,
  ProcessTable,
  ProcessTableHeader,
  ProcessTableRow,
  ProcessName,
  ProcessType,
  ProcessStatus,
  MyProcessList,
  InventoryItem,
  InventoryItemLabel,
  InventoryItemValue,
  InventoryNotification,
  InventoryList,
  InventoryListContainer,
  InventoryHeader,
  InventoryAction,
  InventoryTableHeader,
  CompanyLogo,
  CompanyBrand,
  CompanyName,
} from './styled'
import { P, SubtitleBold } from 'views/components/UI/Text'
import Select from 'views/components/UI/Select'
import { Button } from 'views/components/UI/Buttons'
import { platformAdminRoutes } from 'router/routes'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import Modal from 'views/components/UI/Modal'
import { useFetchAllProcesses } from 'hooks/process/useGetAllProcessCurrentCompany/useGetAllProcessCurrentCompany'
import { useGetResumeProcess } from 'hooks/process/useGetResumeProcess/useGetResumeProcess'
import { ProcessTableActions } from './components'
import { cleanProcess } from 'ducks/createProcess'
import { useNotification } from 'lib/context/notification.context'
import { useFinishProcess } from 'hooks/process/useFinishProcess/useFinishProcess'
import { getProcessDetail } from 'services/common/process/createProcess/create-process-service'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { userSelector } from 'ducks/user'
import { Subtitle } from 'views/components/UI/StyledComponents/styled.components'
import dayjs from 'dayjs'
import fetchActiveInventories, { ActiveInventory } from './services/fetch-active-inventories'
import { requestRenewalEmail } from 'services/inventory/requestRenewalEmail'
import getOneCompany from 'services/companies/getOneCompany'

const HomePlatformAdministrator = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { companyId } = useSelector(userSelector)
  const role = useSelector((state: any) => state.auth.role)
  const { getError, getWarning } = useNotification()
  const { allProcess, fetchAllProcesses, loadingAllProcesses } = useFetchAllProcesses()

  const { handleFinishProcess } = useFinishProcess()

  const { loadingDetailProcess, processInfo, fetchProcessData, fetchTestsByCompany, testsByCompany } =
    useGetResumeProcess()
  const [process, setProcess] = useState<number | ''>('')
  const [loadingFinishProcess, setLoadingFinishProcess] = useState(false)
  const [useProcess, setUseProcess] = useState<number | number[]>()
  const [allProcessSelect, setAllProcessSelect] = useState([] as any)
  const [activeInventories, setActiveInventories] = useState<ActiveInventory[]>([])
  const [companyLogo, setCompanyLogo] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [renewalRequest, setRenewalRequest] = useState<'sending' | 'success' | 'error' | null>(null)

  const handleRenewInventory = async (inventory: ActiveInventory) => {
    try {
      setRenewalRequest('sending')
      await requestRenewalEmail({
        mail: 'sararuiz@bluesitehr.com',
        type: 'inventory_renewal_request',
        company: companyName || 'Empresa no disponible',
        inventoryName: inventory.name,
        inventoryStatus: 'Activo',
        availableCredits: inventory.isUnlimited ? 'Ilimitado' : inventory.creditsAvailable,
        usedCredits: inventory.usedCredits,
        expirationDate: dayjs(inventory.expirationDate).format('DD/MM/YYYY'),
        message: 'El administrador solicita gestionar la renovación del inventario durante 12 meses.',
      })
      setRenewalRequest('success')
      window.setTimeout(() => setRenewalRequest(null), 3500)
    } catch (error) {
      setRenewalRequest('error')
    }
  }

  const getInventoryWarning = (inventory: ActiveInventory): string | null => {
    const availablePercent = inventory.isUnlimited || inventory.credits <= 0
      ? 100
      : (inventory.creditsAvailable / inventory.credits) * 100
    const daysToExpiration = dayjs(inventory.expirationDate).startOf('day').diff(dayjs().startOf('day'), 'day')

    if (!inventory.isUnlimited && availablePercent <= 10) {
      return `El inventario ${inventory.name} tiene el 10% o menos disponible.`
    }

    if (daysToExpiration >= 0 && daysToExpiration <= 30) {
      return `El inventario ${inventory.name} vence en ${daysToExpiration} días.`
    }

    if (!inventory.isUnlimited && availablePercent <= 30) {
      return `El inventario ${inventory.name} tiene el 30% o menos disponible.`
    }

    if (daysToExpiration > 30 && daysToExpiration <= 90) {
      return `El inventario ${inventory.name} vence en menos de 3 meses.`
    }

    return null
  }

  useEffect(() => {
    if (process === '') {
      fetchAllProcesses()
      fetchTestsByCompany(process)
    } else {
      fetchTestsByCompany(process)
    }
  }, [process, fetchAllProcesses, fetchTestsByCompany])

  useEffect(() => {
    const processIDs = allProcess?.map((processFilter) => processFilter.value)
    const allProcessFiltered = allProcess?.filter(
      (item: any, index, self) => index === self.findIndex((ts: any) => ts.process_id === item.process_id)
    )
    setAllProcessSelect(allProcessFiltered)
    const numberProcessIDs = processIDs?.map((id) => Number(id))
    const processToSend = !process ? numberProcessIDs : process

    if (processIDs?.length === 0 || !processToSend) {
      return
    }
    setUseProcess(processToSend)
    fetchProcessData(processToSend)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allProcess, fetchProcessData, process])

  useEffect(() => {
    if (!['platform_admin', 'super_platform_admin'].includes(role) || !companyId) {
      setActiveInventories([])
      setCompanyLogo('')
      return
    }

    fetchActiveInventories(companyId).then(setActiveInventories)
    getOneCompany(companyId).then((response: any) => {
      setCompanyLogo(response?.data?.logo || '')
      setCompanyName(response?.data?.name || response?.data?.company_name || '')
    })
  }, [companyId, role])

  const handleCreateProcess = () => {
    dispatch(cleanProcess())
    setTimeout(() => {
      navigate(platformAdminRoutes.CreateProcess)
    }, 1000)
  }

  const onFinishProcess = useCallback(
    (processId: number) => {
      if (!loadingFinishProcess) {
        setLoadingFinishProcess(true)
        getProcessDetail(processId)
          .then((response: any) => {
            setLoadingFinishProcess(false)
            handleFinishProcess(response.process)
          })
          .catch(() => {
            getError('Ha ocurrido un error al cargar este proceso')
            setLoadingFinishProcess(false)
          })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getError]
  )

  const handleSelectChange = (e: any) => {
    if (e) {
      setProcess(parseInt(e))
    } else {
      setProcess('')
    }
  }

  const handleSelectClick = () => {
    if (allProcess.length === 0) {
      getWarning('No tienes procesos asignados')
    }
  }

  let processesToShow: Array<{
    process_id: number
    process_name: string
    active_process: boolean
    testId: number
    test_name: string
  }> = []

  if (processInfo && processInfo.all_processes.length > 0) {
    processesToShow = _.uniqBy(processInfo.all_processes, 'process_id')
    processesToShow = processesToShow.slice(0, 10)
  }

  const generatedTests = testsByCompany?.totalTests || 0
  const completedTests = processInfo?.total_tests_completed || 0
  const pendingTests = Math.max(generatedTests - completedTests, 0)
  const completionPercent = generatedTests > 0 ? (completedTests / generatedTests) * 100 : 0
  const testCategories = [
    { label: 'Betesa', value: testsByCompany?.categories.Betesa || 0, color: '#c6016b' },
    { label: t('global.common.skillText'), value: testsByCompany?.categories.Skills || 0, color: '#5da9d3' },
    { label: t('global.common.threeSixtyText'), value: testsByCompany?.categories.Test360 || 0, color: '#2bc48a' },
    { label: 'En borrador', value: testsByCompany?.draftTests || 0, color: '#c8d2dc' },
  ]

  const getProcessTestTypes = (processId: number) => {
    const names = processInfo?.all_processes
      ?.filter((entry: any) => entry.process_id === processId)
      .map((entry: any) => String(entry.test_name || '').toLowerCase()) || []
    const labels = new Set<string>()
    if (names.some((name) => name.includes('betesa'))) labels.add('Betesa')
    if (names.some((name) => name.includes('compet') || name.includes('skill'))) labels.add('3D Skills')
    if (names.some((name) => name.includes('360'))) labels.add('360°')
    return Array.from(labels).join(' + ') || '—'
  }

  return (
    <MainLayout>
      {loadingAllProcesses || loadingDetailProcess ? (
        <ModalLoader isLoading={loadingAllProcesses || loadingDetailProcess} />
      ) : (
        <Container>
          <Modal show={Boolean(renewalRequest)} setShow={() => setRenewalRequest(null)}>
            <div style={{ padding: '24px', textAlign: 'center' }}>
              <h3>{renewalRequest === 'sending' ? 'Comunicando con Bluesite' : renewalRequest === 'success' ? 'Petición de renovación enviada' : 'No se pudo enviar la petición'}</h3>
              <p>{renewalRequest === 'sending' ? 'Estamos enviando la solicitud.' : renewalRequest === 'success' ? 'El equipo de BlueSite ha recibido la información para gestionarla.' : 'Contacta con BlueSite para notificar el problema.'}</p>
            </div>
          </Modal>
          <TopContainer>
            <CompanyBrand>
              {companyLogo && <CompanyLogo src={companyLogo} alt="Logo de la empresa" />}
              {companyName && <CompanyName>{companyName}</CompanyName>}
            </CompanyBrand>
            <Button onClick={handleCreateProcess} type="submit">
              Nuevo proceso
            </Button>
          </TopContainer>
          {(role === 'platform_admin' || role === 'super_platform_admin') && (
            <InventoryListContainer>
              <InventoryHeader>
                <Subtitle>Inventarios activos</Subtitle>
                <InventoryAction
                  to={platformAdminRoutes.Inventory.replace(':companyId', companyId.toString())}
                  state={process ? { processSelectedId: process } : {}}
                >
                  Ver inventarios <span aria-hidden="true">→</span>
                </InventoryAction>
              </InventoryHeader>
              <InventoryTableHeader>
                <strong>Inventario</strong>
                <strong>Disponibles</strong>
                <strong>Utilizados</strong>
                <strong>Vencimiento</strong>
                <strong>Notificaciones</strong>
                <strong>Acciones</strong>
              </InventoryTableHeader>
              <InventoryList>
                {activeInventories.length > 0 ? (
                  activeInventories.map((inventory) => {
                    const warning = getInventoryWarning(inventory)

                    return (
                      <InventoryItem key={inventory.id}>
                        <InventoryItemLabel>{inventory.name}</InventoryItemLabel>
                        <InventoryItemValue>
                          Disponibles: {inventory.isUnlimited ? 'Ilimitado' : inventory.creditsAvailable}
                        </InventoryItemValue>
                        <InventoryItemValue>
                          Utilizados: {inventory.usedCredits}
                        </InventoryItemValue>
                        <InventoryItemValue>
                          Vence: {dayjs(inventory.expirationDate).format('DD/MM/YYYY')}
                        </InventoryItemValue>
                        <InventoryNotification warning={Boolean(warning)}>
                          {warning || 'Sin notificaciones'}
                        </InventoryNotification>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => handleRenewInventory(inventory)}
                          disabled={renewalRequest === 'sending'}
                        >
                          Solicitar renovación
                        </Button>
                      </InventoryItem>
                    )
                  })
                ) : (
                  <InventoryItemValue>No hay inventarios activos.</InventoryItemValue>
                )}
              </InventoryList>
            </InventoryListContainer>
          )}
          <BigContainer>
            <div style={{ display: 'flex', flexDirection: 'row', gap: 16, alignItems: 'center', gridColumn: '1 / -1' }}>
              <SubtitleBold>Visualizar:</SubtitleBold>
              <div style={{ width: 360, height: 48, flex: '0 0 360px' }}>
                <Select
                  label="Todos los procesos"
                  showLabel={false}
                  options={allProcessSelect || []}
                  value={process}
                  onChange={(e) => handleSelectChange(e)}
                  onClick={() => handleSelectClick()}
                />
              </div>
            </div>
            <MetricsGrid>
              <MetricCard>
                <MetricIcon color="#c6016b">▤</MetricIcon>
                <div><MetricLabel>Pruebas generadas</MetricLabel><MetricValue>{generatedTests}</MetricValue></div>
              </MetricCard>
              <MetricCard>
                <MetricIcon color="#2d749c">✓</MetricIcon>
                <div><MetricLabel>Pruebas contestadas</MetricLabel><MetricValue>{completedTests}</MetricValue></div>
              </MetricCard>
              <MetricCard>
                <MetricIcon color="#d99a00">◷</MetricIcon>
                <div><MetricLabel>Pendientes</MetricLabel><MetricValue>{pendingTests}</MetricValue></div>
              </MetricCard>
              <MetricCard>
                <MetricIcon color="#2bc48a">▥</MetricIcon>
                <div><MetricLabel>Completado</MetricLabel><MetricValue>{completionPercent.toFixed(1).replace('.', ',')}%</MetricValue></div>
              </MetricCard>
            </MetricsGrid>
            <AnalyticsCard>
              <AnalyticsCardTitle>Pruebas generadas por tipo</AnalyticsCardTitle>
              <AnalyticsCardSubtitle>Total de {generatedTests} pruebas generadas, desglosadas por tipo.</AnalyticsCardSubtitle>
              <BarsList>
                {testCategories.map((category) => (
                  <BarRow key={category.label}>
                    <BarLabel>{category.label}</BarLabel>
                    <BarTrack>
                      <BarFill
                        $color={category.color}
                        $width={generatedTests ? `${(category.value / generatedTests) * 100}%` : '0%'}
                      />
                    </BarTrack>
                    <BarValue>{category.value}</BarValue>
                  </BarRow>
                ))}
              </BarsList>
            </AnalyticsCard>
            <AnalyticsCard>
              <AnalyticsCardTitle>Progreso de respuestas</AnalyticsCardTitle>
              <AnalyticsCardSubtitle>Porcentaje de pruebas contestadas sobre el total generado.</AnalyticsCardSubtitle>
              <ProgressValue>{completionPercent.toFixed(1).replace('.', ',')}%</ProgressValue>
              <ProgressCaption>{completedTests} de {generatedTests} pruebas contestadas</ProgressCaption>
              <ProgressTrack><ProgressFill $width={`${completionPercent}%`} /></ProgressTrack>
              <ProgressLegend>
                <LegendItem><LegendDot color="#2bc48a" />Contestadas <strong>{completedTests}</strong></LegendItem>
                <LegendItem><LegendDot color="#c8d2dc" />Pendientes <strong>{pendingTests}</strong></LegendItem>
              </ProgressLegend>
            </AnalyticsCard>

            <MyProcessContainer>
              <InventoryHeader>
                <Subtitle>Mis procesos</Subtitle>
                <InventoryAction to={platformAdminRoutes.MyProcess} state={useProcess}>
                  Ver todos los procesos
                </InventoryAction>
              </InventoryHeader>
              <MyProcessList>
                {processInfo && processInfo.all_processes.length > 0 ? (
                  <ProcessTable>
                    <ProcessTableHeader>
                      <strong>Proceso</strong>
                      <strong>Tipo de pruebas</strong>
                      <strong>Estado</strong>
                      <strong>Acciones</strong>
                    </ProcessTableHeader>
                    {_.uniqBy(processesToShow, 'process_id').map((item) => (
                      <ProcessTableRow key={item.process_id}>
                        <ProcessName>{item.process_name}</ProcessName>
                        <ProcessType>{getProcessTestTypes(item.process_id)}</ProcessType>
                        <ProcessStatus
                          color={item.active_process ? '#16834b' : '#9a6700'}
                          background={item.active_process ? '#d9f7e7' : '#fff0c7'}
                        >
                          {item.active_process ? 'Enviado' : 'Borrador'}
                        </ProcessStatus>
                        <ProcessTableActions
                          active={item.active_process}
                          onFinishProcess={onFinishProcess}
                          processId={item.process_id}
                          name={item.process_name}
                        />
                      </ProcessTableRow>
                    ))}
                  </ProcessTable>
                ) : (
                  <P>No existen procesos asociados a esta compañía.</P>
                )}
              </MyProcessList>
            </MyProcessContainer>
          </BigContainer>
        </Container>
      )}
    </MainLayout>
  )
}

export default HomePlatformAdministrator
